// react-router components
import { Link } from "react-router-dom";

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftAvatar from "components/SoftComponents/SoftAvatar";
import SoftBadge from "components/SoftComponents/SoftBadge";
import { Box, Divider, Grid, Icon } from "@mui/material";
import ProductImages from "template/layouts/ecommerce/products/product-page/components/ProductImages";
import ProductInfo from "template/layouts/ecommerce/products/product-page/components/ProductInfo";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftSelect from "components/SoftComponents/SoftSelect";

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

import moment from "moment";
import { useEffect, useRef } from "react";

import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { Close } from "@mui/icons-material";

import badgeDict from "modules/inspections/components/badgeDict.json";
import SprayCan from "assets/images/spray_can_white.png";
import BrokenDevice from "assets/images/broken_device_white.png";

import { useAuth0 } from "@auth0/auth0-react";
import inspectionsAPI from "modules/inspections/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSoftUIController } from "hooks/context";
import Swal from "sweetalert2";

function getStatusColour(status) {
    var colour = "secondary";

    if (status == 'review') {
        colour = "warning";
    }
    else if (status == 'inspected') {
        colour = "info";
    }
    else if (status == "unresolved") {
        colour = "error";
    }
    else if (status == "resolved") {
        colour = "success";
    }

    return colour;
}

function InspectionLiveviewCard({ device }) {

    inspectionsAPI.loadToken(useAuth0());

    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    return (
        <Card sx={{ height: '100%', overflowY: 'auto' }}>
            <SoftBox display="flex" flexDirection="column" justifyContent="space-between" p={1}>
                <SoftBox>
                    <PhotoProvider key={device.inspection_record_id}>
                        <SoftBox p={deviceLayout == 'mobile' ? 0.5 : 1}>
                            <Grid container>
                                <Grid item xxs={12}>
                                    <SoftBox>
                                        <SoftBox mb={1} pr={0.5}>
                                            <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" textTransform="capitalize">
                                                {device.title}
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBadge variant="contained" color={getStatusColour(device.status)} badgeContent={device.status} container />
                                        <SoftBox mt={2}>
                                            <SoftTypography variant="button" fontWeight="bold">
                                                Date recorded
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox mb={1} ml={0.5}>
                                            <SoftTypography variant={"body4"} fontWeight="medium">
                                                {moment(device?.inspectionCreatedAt || 0).format("DD/MM/YY hh:mm a")}
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox mt={3} mb={1}>
                                            <SoftTypography variant="button" fontWeight="bold">
                                                Additional Information
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox pl={0.5} pr={0.5}>
                                            <SoftTypography variant="body2" color="text">
                                                {device.description ? device.description : "No additional information added."}
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>
                                </Grid>
                            </Grid>
                        </SoftBox>

                        <Box>
                            <SoftBox p={deviceLayout == 'mobile' ? 0 : 0.5}>
                                <Divider />
                                {(device.images.length > 0 && device.images.find((obj) => obj.uuid == device.uuid) != null) ? (
                                    <SoftBox mt={(device.conditions || device.actions) ? 3 : 1}>
                                        <SoftBox mb={1} ml={0.5}>
                                            <SoftTypography variant="button" fontWeight="bold">
                                                Site Images
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox p={1} sx={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                                            <Grid container spacing={1}>
                                                {device.images.find((obj) => obj.uuid == device.uuid).urls.map((img, imgIndex) => {
                                                    var length = device.images.find((obj) => obj.uuid == device.uuid).urls.length;

                                                    if (imgIndex >= 3) return;

                                                    if (imgIndex != 2) {
                                                        return (
                                                            <Grid item key={imgIndex} xxs={4}>
                                                                <PhotoView src={img}>
                                                                    <SoftBox
                                                                        component="img"
                                                                        src={img}
                                                                        width="100%"
                                                                        height="200px"
                                                                        sx={{
                                                                            objectFit: 'cover',
                                                                            borderRadius: '5px',
                                                                            cursor: 'pointer',
                                                                            transition: "box-shadow 0.3s ease-in-out",
                                                                            "&:hover": {
                                                                                boxShadow: "0 0 15px 2px rgba(0, 0, 0, 0.3)",
                                                                            }
                                                                        }}
                                                                    />
                                                                </PhotoView>
                                                            </Grid>
                                                        );
                                                    }
                                                    else {
                                                        return (
                                                            <Grid item key={imgIndex} xxs={4}>
                                                                <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                                                                    <Box><PhotoView src={img}>
                                                                        <SoftBox
                                                                            component="img"
                                                                            src={img}
                                                                            width="100%"
                                                                            height="200px"
                                                                            sx={{
                                                                                objectFit: 'cover',
                                                                                borderRadius: '5px',
                                                                                cursor: 'pointer',
                                                                                transition: "box-shadow 0.3s ease-in-out",
                                                                                "&:hover": {
                                                                                    boxShadow: "0 0 15px 2px rgba(0, 0, 0, 0.3)",
                                                                                }
                                                                            }}
                                                                        />
                                                                    </PhotoView></Box>
                                                                    {(length - 3 > 0) && (
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                                                            fontSize: '10px',
                                                                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                                            top: 0,
                                                                            left: 0,
                                                                            display: 'flex',
                                                                            color: '#fff',
                                                                            width: '100%',
                                                                            height: '200px',
                                                                            borderRadius: '5px',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'center'
                                                                        }}>
                                                                            {length - 3}+ more..
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Grid>
                                                        );
                                                    }
                                                })}
                                            </Grid>
                                        </SoftBox>
                                    </SoftBox>
                                ) : (
                                    <SoftBox mt={(device?.conditions || device?.actions) ? 3 : 1}>
                                        <SoftBox mb={1} ml={0.5}>
                                            <SoftTypography variant="button" fontWeight="bold">
                                                Site Images
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox p={1} sx={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                                            <SoftTypography variant="body3" fontWeight="regular">
                                                No site images to show.
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>
                                )}
                            </SoftBox>
                        </Box>
                        <SoftButton variant="contained" color="light" fullWidth component={Link} to={`../operations/${device.inspection_record_id}`}><SoftTypography pr={0.5} variant="button" color="light_theme">View Inspection</SoftTypography><Icon>arrow_forward</Icon></SoftButton>
                    </PhotoProvider>
                </SoftBox>
            </SoftBox>
        </Card >
    );
}

// Typechecking props for the InspectionLiveviewCard
InspectionLiveviewCard.propTypes = {
    device: PropTypes.object
};

export default InspectionLiveviewCard;